import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { selectEquipmentDetail } from "../../store/informationSlice";
import { fetchChartData, selectInfoData } from "../../store/informationSlice";
import { selectWsData } from "../../store/wsSlice";

export default function Equipment(props) {
  const dispatch = useDispatch();
  const infoData = useSelector(selectInfoData);
  const wsData = useSelector(selectWsData);

  const { setCurrentLayer } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const equipmentDetail = useSelector(selectEquipmentDetail);
  const [path, setPath] = useState([]);
  const [searchResult, setSearchResult] = useState([]);
  const [searching, setSearching] = useState(false);

  const [searchKeyword, setSearchKeyword] = useState("");

  // 生成層級標題的輔助函數
  const getLevelHeaders = (maxLevels) => {
    const headers = [];
    for (let i = 1; i <= maxLevels; i++) {
      headers.push(`第${i}層`);
    }
    return headers;
  };
  // 找到節點的完整路徑
  const findNodePath = useCallback((targetNode, rootNodes) => {
    const findPath = (nodes, target, currentPath = []) => {
      for (const node of nodes) {
        const newPath = [...currentPath, node];

        if (node.id === target.id) {
          return newPath;
        }

        if (node.list && node.list.length > 0) {
          const foundPath = findPath(node.list, target, newPath);
          if (foundPath) {
            return foundPath;
          }
        }
      }
      return null;
    };

    return findPath(rootNodes, targetNode);
  }, []);
  // 先計算 displayedLeafPaths 和 maxLevels，避免在 early return 後調用 hooks
  const current = path.length > 0 ? path[path.length - 1] : null;
  const children = current?.list || [];
  const atRoot = path.length === 1;
  const isLeaf = searching || children.length === 0;

  const flattenEveryNodePath = useCallback((node, currentPath = []) => {
    const newPath = [...currentPath, node];
    let result = [newPath];

    if (node?.list && node.list.length > 0) {
      node.list.forEach((child) => {
        result = result.concat(flattenEveryNodePath(child, newPath));
      });
    }

    return result;
  }, []);

  const displayedLeafPaths = useMemo(() => {
    if (searching) {
      return searchResult;
    }
    if (path.length > 0 && path[0]) {
      return flattenEveryNodePath(path[0]);
    }
    return [];
  }, [searching, searchResult, path, flattenEveryNodePath]);

  // 計算最大層級數
  const maxLevels = useMemo(() => {
    if (!displayedLeafPaths || displayedLeafPaths.length === 0) return 5; // 預設值

    const maxDepth = Math.max(
      ...displayedLeafPaths.map((nodes) => nodes.length)
    );
    return Math.max(maxDepth, 1); // 至少要有一層
  }, [displayedLeafPaths]);

  useEffect(() => {
    if (path && path.length > 0) {
      const lastNode = path[path.length - 1]; // 取最後一筆
      setCurrentLayer({
        id: lastNode.id,
        level: lastNode.level,
        name: lastNode.name,
      });
      // 更新網址上的 query string
      const searchParams = new URLSearchParams(location.search);
      searchParams.set("layer", lastNode.name);
      dispatch(
        fetchChartData({ mode: 2, type: 1, equipment_id: lastNode.level })
      );
      navigate(
        {
          pathname: location.pathname,
          search: `?${searchParams.toString()}`,
        },
        { replace: true }
      ); // replace 避免污染瀏覽器返回紀錄
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  useEffect(() => {
    if (equipmentDetail && equipmentDetail.length > 0) {
      setPath([equipmentDetail[0]]);
    }
  }, [equipmentDetail]);

  // Early return if no equipment data
  if (!equipmentDetail || equipmentDetail.length === 0) {
    return <div className="text-white">Loading equipment data...</div>;
  }

  const handleClick = (node) => {
    setPath([...path, node]);
    setSearching(false);
    setSearchKeyword("");
  };

  const handleBreadcrumbClick = (index) => {
    if (index === -1) {
      // Go back one level
      if (path.length > 1) {
        setPath(path.slice(0, path.length - 1));
      }
    } else {
      setPath(path.slice(0, index + 1));
    }
    setSearching(false);
    setSearchKeyword("");
  };

  const handleSearchRealtime = (targetNode) => {
    // 找到目標節點的完整路徑
    const nodePath = findNodePath(targetNode, equipmentDetail);

    if (nodePath) {
      // 直接設置完整路徑，而不是添加到現有路徑
      setPath(nodePath);

      // 退出搜尋模式
      if (searching) {
        setSearching(false);
        setSearchKeyword("");
      }
    }

    // 更新 currentLayer
    setCurrentLayer({
      id: targetNode.id,
      level: targetNode.level,
      name: targetNode.name,
    });

    // 發送 API 請求
    dispatch(
      fetchChartData({
        mode: 2,
        type: 1,
        equipment_id: targetNode.level,
      })
    );

    // 更新 URL
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("layer", targetNode.name);

    navigate(
      {
        pathname: location.pathname,
        search: `?${searchParams.toString()}`,
      },
      { replace: true }
    );
  };

  const handleSearch = (keyword) => {
    if (!keyword?.trim()) {
      setSearching(false);
      setSearchResult([]);
      return;
    }

    const matches = [];
    const searchKeywordLower = keyword.toLowerCase();

    const search = (node, currentPath = []) => {
      const newPath = [...currentPath, node];
      if (node.name && node.name.toLowerCase().includes(searchKeywordLower)) {
        matches.push(newPath);
      }
      if (node.list?.length) {
        node.list.forEach((child) => search(child, newPath));
      }
    };

    equipmentDetail.forEach((root) => search(root));
    setSearchResult(matches);
    setSearching(true);

    // ✅ 搜尋送出後，切回第一層並更新網址與 currentLayer
    const firstLayer = equipmentDetail[0];
    if (firstLayer) {
      setPath([firstLayer]);
      setCurrentLayer({
        id: firstLayer.id,
        level: firstLayer.level,
        name: firstLayer.name,
      });

      const searchParams = new URLSearchParams(location.search);
      searchParams.set("layer", firstLayer.name);

      navigate(
        {
          pathname: location.pathname,
          search: `?${searchParams.toString()}`,
        },
        { replace: true }
      );
    }
  };

  const handleSearchInputChange = (value) => {
    setSearchKeyword(value);

    // 🔑 只控制搜尋狀態，不切換層級
    if (!value.trim()) {
      setSearching(false);
      setSearchResult([]);
    }
  };

  // 取得對應數值（先 infoData，後 wsData 覆蓋）
  const getRealtimeValue = (child, type_id) => {
    // 先找 infoData
    const layerInfo = infoData?.find((item) => item.id === child.id);
    const infoVal = layerInfo?.data?.find((d) => d.type_id === type_id)?.value;

    // 再找 wsData 覆蓋
    const wsVal = Array.isArray(wsData)
      ? wsData
          .find((item) => item.id === child.id)
          ?.data?.find((d) => d.type_id === type_id)?.value
      : wsData?.id === child.id
      ? wsData.data?.find((d) => d.type_id === type_id)?.value
      : undefined;

    return wsVal !== undefined ? wsVal : infoVal;
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      {/* Breadcrumb Navigation */}
      <div className="flex items-center gap-4 overflow-x-auto no-scrollbar">
        {atRoot ? (
          <>
            {/* First level button */}
            <button
              key={path[0]?.id}
              onClick={() => handleBreadcrumbClick(0)}
              className="text-white px-3 py-[10px] h-[40px] font-bold text-[16px] lg:text-[20px] rounded-[12px] flex items-center justify-center min-w-[108px] lg:min-w-[120px] bg-[#489A96]"
            >
              {path[0]?.name}
            </button>

            {/* Second level buttons */}
            {children.map((child) => (
              <button
                key={child.id}
                onClick={() => handleClick(child)}
                className="text-white px-3 py-[10px] h-[40px] font-bold text-[16px] lg:text-[20px] rounded-[12px] flex items-center justify-center min-w-[108px] lg:min-w-[120px] bg-[#1A111D]/70 hover:bg-[#1A111D]/90 transition-colors"
              >
                {child.name}
              </button>
            ))}
          </>
        ) : (
          <>
            {path.map((node, index) => {
              const isCurrent = index === path.length - 1;
              return (
                <button
                  key={`${node.id}-${index}`}
                  onClick={() => !isCurrent && handleBreadcrumbClick(index)}
                  disabled
                  className={`px-3 py-[10px] h-[40px] font-bold text-[16px] lg:text-[20px]
                    rounded-[12px] flex items-center justify-center min-w-[108px] lg:min-w-[120px]
                    ${
                      isCurrent
                        ? "bg-[#489A96] text-white cursor-default"
                        : "bg-[#808080] text-white hover:bg-[#606060] cursor-pointer transition-colors"
                    }`}
                >
                  {node.name}
                </button>
              );
            })}

            {/* Back button */}
            <button
              onClick={() => handleBreadcrumbClick(-1)}
              className="text-[#4A3969] bg-white px-3 py-[10px] h-[40px] font-bold text-[16px] lg:text-[20px] rounded-[12px] flex items-center justify-center min-w-[108px] lg:min-w-[120px] hover:bg-gray-100 transition-colors"
            >
              回上一層
            </button>
          </>
        )}

        {/* Search input */}
        <div className="flex items-center px-3 bg-[#1A111D]/70 rounded-[12px] h-[40px] min-w-[120px] w-[192px] ml-auto">
          <input
            type="text"
            placeholder="搜尋"
            value={searchKeyword}
            onChange={(e) => handleSearchInputChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSearch(searchKeyword);
              }
            }}
            className="bg-transparent text-sm w-full text-[#CECECE] placeholder-[#CECECE] rounded-l-[12px] outline-none px-2"
          />
          <button
            onClick={() => handleSearch(searchKeyword)}
            className="p-1 hover:bg-white/10 rounded transition-colors"
            aria-label="搜尋"
          >
            <img
              src="/assets/icons/search.svg"
              alt="搜尋"
              className="w-4 h-4"
            />
          </button>
        </div>
      </div>

      {/* Equipment Cards or Table */}
      {isLeaf ? (
        <div className="w-full overflow-x-auto">
          {displayedLeafPaths.length === 0 && searching ? (
            // 搜索无结果时显示提示信息
            <div className="text-white text-center py-8">
              <div className="text-lg font-semibold mb-2">查無此終端設備</div>
              <div className="text-gray-400">請嘗試其他關鍵字搜索</div>
            </div>
          ) : (
            <table className="table-auto w-full text-white border-separate border-spacing-y-2 min-w-[1200px]">
              <thead>
                <tr className="font-bold text-center">
                  <th className="px-2 py-2 whitespace-nowrap">編號</th>
                  {/* 動態生成層級標題 */}
                  {getLevelHeaders(maxLevels).map((header, index) => (
                    <th
                      key={index}
                      className="px-2 py-2 whitespace-nowrap min-w-[100px]"
                    >
                      {header}
                    </th>
                  ))}
                  <th className="px-2 py-2 whitespace-nowrap min-w-[120px]">
                    建築位置
                  </th>
                  <th className="px-2 py-2 whitespace-nowrap min-w-[150px]">
                    終端設備名稱
                  </th>
                  <th className="px-2 py-2 whitespace-nowrap min-w-[120px]">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayedLeafPaths.map((nodes, index) => {
                  const node = nodes[nodes.length - 1];
                  if (!node) return null;

                  return (
                    <tr
                      key={`${node.id}-${index}`}
                      className="text-center hover:bg-white/10 transition-colors"
                    >
                      <td className="py-2 font-semibold rounded-l-lg border border-r-0 border-[#41B8EC] flex items-center justify-center whitespace-nowrap">
                        <span className="border border-[#41B8EC] rounded-full w-8 h-8 flex items-center justify-center p-2">
                          {index + 1}
                        </span>
                      </td>
                      {/* 動態生成層級欄位 */}
                      {Array.from({ length: maxLevels }).map((_, i) => (
                        <td
                          key={i}
                          className="py-2 border-y border-[#41B8EC] whitespace-nowrap px-3"
                        >
                          <span
                            className="truncate block max-w-[100px] mx-auto text-center"
                            title={nodes[i]?.name || "-"}
                          >
                            {nodes[i]?.name || "-"}
                          </span>
                        </td>
                      ))}
                      <td className="py-2 border-y border-[#41B8EC] whitespace-nowrap px-3">
                        <span
                          className="truncate block max-w-[120px] mx-auto text-center"
                          title={node.area || "-"}
                        >
                          {node.area || "-"}
                        </span>
                      </td>
                      <td className="py-2 border-y border-[#41B8EC] whitespace-nowrap px-3">
                        <span
                          className="truncate block max-w-[150px] mx-auto text-center"
                          title={node.name || "-"}
                        >
                          {node.name || "-"}
                        </span>
                      </td>
                      <td className="py-2 rounded-r-lg border border-l-0 border-[#41B8EC] whitespace-nowrap">
                        <button
                          onClick={() => handleSearchRealtime(node)}
                          className="bg-[#3768F3] px-3 py-1 rounded text-white text-sm hover:bg-[#2557E2] transition-colors whitespace-nowrap"
                        >
                          查詢即時用電
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        <div className="flex overflow-x-auto md:overflow-visible md:flex-wrap w-full items-center justify- gap-4">
          {children.map((child) => {
            if (!child) return null;

            const power = getRealtimeValue(child, "c1");
            const voltage = getRealtimeValue(child, "c2");
            const currentVal = getRealtimeValue(child, "c4");
            const totalPower = getRealtimeValue(child, "c7");

            return (
              <div
                key={child.id}
                onClick={() => handleClick(child)}
                className="min-w-[263px] h-[342px] bg-black text-white border border-white shadow-[0_0_6px_1px_rgba(255,255,255,0.3)] flex flex-col overflow-hidden cursor-pointer hover:shadow-[0_0_10px_2px_rgba(0,255,255,0.4)] transition-shadow"
              >
                {/* Title */}
                <div className="h-[28px] flex items-center justify-center border-b border-white text-base font-semibold">
                  {child.name}
                </div>

                {/* Image + Data Section */}
                <div className="relative h-[164px] bg-[#2D214F] border-b border-white flex items-center justify-center overflow-hidden">
                  {/* Left circle */}
                  <div className="absolute left-[10px] top-1/2 -translate-y-1/2 z-10">
                    <div className="border border-white w-[18px] h-[18px] rounded-full" />
                  </div>

                  {/* Right circle */}
                  <div className="absolute right-[10px] top-1/2 -translate-y-1/2 z-10">
                    <div className="border border-white w-[18px] h-[18px] rounded-full" />
                  </div>

                  {/* Background image */}
                  <img
                    src="/assets/images/card-bg.png"
                    alt="card-bg"
                    className="absolute top-1/2 left-1/2 w-[210px] h-[210px] -translate-x-1/2 -translate-y-1/2 object-contain"
                  />

                  <div className="relative z-10 flex flex-col items-center justify-center w-[144px] h-[144px] drop-shadow">
                    <span className="mx-auto text-[14px]">順流</span>
                    <span className="text-[24px] h-[48px] flex items-center justify-center">
                      {power ?? "--"}
                    </span>
                    <span className="mx-auto text-[14px]">CMH</span>
                  </div>
                </div>

                <div className="h-[92px] flex p-[2px] text-sm gap-[2px] border-b border-[#66E6F9]">
                  <div className="w-1/2 flex flex-col items-center justify-center bg-[#1E3A8A] gap-1 border border-[#66E6F9]">
                    <span className="text-[20px]">當日用水</span>
                    <span className="text-[28px]">{voltage ?? "--"}</span>
                    <span className="text-white/70 text-[16px]">{"度"}</span>
                  </div>

                  <div className="w-1/2 flex flex-col items-center bg-[#1E3A8A] justify-center gap-1 border border-[#66E6F9]">
                    <span className="text-[20px]">當月用水</span>
                    <span className="text-[28px]">{currentVal ?? "--"}</span>
                    <span className="text-white/70 text-[16px]">{"度"}</span>
                  </div>
                </div>

                <div className="p-[2px] flex items-center justify-center">
                  <div className="bg-[#1E3A8A] flex w-full p-2 h-full border border-[#66E6F9]">
                    <div className="flex flex-col w-[70%] h-full">
                      <span className="text-[16px]">累積總度數</span>
                      <span className="text-[24px]">{totalPower ?? "--"}</span>
                    </div>
                    <div className="w-[30%] text-[16px] text-white/70 h-full flex items-end justify-end">
                      度
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
